import { h } from '@/lib/dom';
import { SkeletonList } from '@/components/Skeleton';
import { Table } from '@/components/Table';
import { toast } from '@/components/Toast';
import { openModal } from '@/components/Modal';
import { data } from '@/lib/data';
import { ApiError } from '@/api/client';
import { documentFileUrl } from '@/api/documents';
import { formatDate, plural } from '@/lib/format';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import { pageLayout } from './layout';
import type { DocumentSummary } from '@/api/types';

const MAX_DESC_LEN = 500;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

export async function renderDocuments(_ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }
  const isOwner = user.role === 'owner';

  const subtitleEl = h('p', { class: 'documents-subtitle', id: 'docs-subtitle' }, 'Загружаем…');
  const contentEl = h('div', { id: 'docs-content' }, SkeletonList(4));

  let docs: DocumentSummary[] = [];

  const uploadBtn = isOwner
    ? h('button', {
        class: 'btn btn-secondary btn-sm',
        type: 'button',
        on: { click: () => openUploadModal() },
      }, '+ Загрузить документ')
    : null;

  const header = h('div', { class: 'documents-header' }, [
    h('div', { class: 'documents-titles' }, [
      h('h1', { class: 'documents-title' }, 'Документы'),
      subtitleEl,
    ]),
    uploadBtn,
  ]);

  const root = pageLayout({
    user,
    active: 'documents',
    children: [
      h('div', { class: 'documents-page' }, [header, contentEl]),
    ],
  });

  const renderDocs = () => {
    contentEl.innerHTML = '';

    if (!docs.length) {
      contentEl.appendChild(renderEmpty(isOwner, () => openUploadModal()));
      return;
    }

    // Desktop table
    const tableWrap = h('div', { class: 'documents-table' },
      h('div', { class: 'data-table-scroll' }, Table({
        columns: [
          {
            key: 'uploaded_at',
            header: 'Дата загрузки',
            render: (row) => formatDate(row['uploaded_at'] as string),
          },
          {
            key: 'filename',
            header: 'Имя файла',
            render: (row) => h('a', {
              href: documentFileUrl(row['id'] as number),
              class: 'doc-filename-link',
              target: '_blank',
              rel: 'noopener',
              'data-external': '',
            }, row['filename'] as string),
          },
          {
            key: 'description',
            header: 'Описание',
            render: (row) => {
              const doc = docs.find((d) => d.id === (row['id'] as number));
              if (!doc) return h('span', { class: 'text-tertiary' }, '—');
              return renderDescCell(doc);
            },
          },
          ...(isOwner ? [{
            key: 'actions',
            header: 'Действия',
            render: (row: Record<string, unknown>) => {
              const doc = docs.find((d) => d.id === (row['id'] as number));
              if (!doc) return h('span');
              return h('div', { class: 'doc-actions-cell' }, [
                h('button', {
                  class: 'btn btn-ghost btn-sm',
                  type: 'button',
                  aria: { label: 'Редактировать описание' },
                  on: { click: () => startInlineEdit(doc) },
                }, '✎'),
                h('button', {
                  class: 'btn btn-ghost btn-sm',
                  type: 'button',
                  aria: { label: 'Удалить документ' },
                  on: { click: () => openDeleteModal(doc) },
                }, '🗑'),
              ]);
            },
          }] : []),
        ],
        rows: docs.map((d) => ({
          id: d.id,
          uploaded_at: d.uploaded_at,
          filename: d.filename,
          description: d.description,
        })),
        ariaLabel: 'Список документов',
      })),
    );

    // Mobile card list
    const cardList = h('ul', { class: 'doc-card-list', role: 'list' },
      docs.map((doc) => renderDocCard(doc, isOwner)),
    );

    contentEl.append(tableWrap, cardList);
  };

  // Inline edit: replace description cell content in table
  const activeEdits = new Map<number, HTMLElement>();

  function renderDescCell(doc: DocumentSummary): HTMLElement {
    if (activeEdits.has(doc.id)) return activeEdits.get(doc.id)!;
    if (!isOwner) {
      return h('span', { class: doc.description ? '' : 'text-tertiary' },
        doc.description ?? '—');
    }
    const span = h('span', { class: doc.description ? '' : 'text-tertiary' },
      doc.description ?? '—');
    return span;
  }

  function startInlineEdit(doc: DocumentSummary): void {
    // Find the td containing the description for this row
    const rows = contentEl.querySelectorAll('tbody tr');
    let targetTd: HTMLElement | null = null;
    rows.forEach((tr) => {
      const tds = tr.querySelectorAll('td');
      // filename td is index 1; check if it contains our filename
      const fileCell = tds[1];
      if (fileCell?.textContent?.trim() === doc.filename) {
        targetTd = tds[2] as HTMLElement ?? null;
      }
    });

    if (!targetTd) return;

    const container = h('div', { class: 'doc-inline-edit' });
    const textarea = h('textarea', {
      class: 'form-textarea',
      'aria-label': 'Описание документа',
      style: 'min-height: 60px;',
    }) as HTMLTextAreaElement;
    textarea.value = doc.description ?? '';

    const counter = h('div', {
      class: 'doc-desc-counter',
      'aria-live': 'polite',
    }, `символов: ${textarea.value.length} / ${MAX_DESC_LEN}`);

    textarea.addEventListener('input', () => {
      if (textarea.value.length > MAX_DESC_LEN) {
        textarea.value = textarea.value.slice(0, MAX_DESC_LEN);
      }
      counter.textContent = `символов: ${textarea.value.length} / ${MAX_DESC_LEN}`;
    });

    const saveDesc = async () => {
      const newDesc = textarea.value.trim() || null;
      try {
        const updated = await data.updateDocument(doc.id, newDesc);
        const idx = docs.findIndex((d) => d.id === doc.id);
        if (idx !== -1) docs[idx] = updated;
        toast('Описание обновлено', { type: 'success' });
      } catch {
        toast('Ошибка сохранения', { type: 'error' });
      }
      activeEdits.delete(doc.id);
      renderDocs();
    };

    const cancelEdit = () => {
      activeEdits.delete(doc.id);
      renderDocs();
    };

    textarea.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); void saveDesc(); }
      if (ev.key === 'Escape') cancelEdit();
    });

    const actions = h('div', { class: 'doc-inline-edit-actions' }, [
      h('button', {
        class: 'btn btn-primary btn-sm',
        type: 'button',
        on: { click: () => void saveDesc() },
      }, 'Сохранить'),
      h('button', {
        class: 'btn btn-secondary btn-sm',
        type: 'button',
        on: { click: cancelEdit },
      }, 'Отмена'),
    ]);

    container.append(textarea, counter, actions);
    activeEdits.set(doc.id, container);

    (targetTd as HTMLElement).innerHTML = '';
    (targetTd as HTMLElement).appendChild(container);
    textarea.focus();
  }

  function renderDocCard(doc: DocumentSummary, ownerMode: boolean): HTMLElement {
    return h('li', { class: 'doc-card' }, [
      h('time', { class: 'doc-card-date', datetime: doc.uploaded_at },
        formatDate(doc.uploaded_at)),
      h('a', {
        href: documentFileUrl(doc.id),
        class: 'doc-card-filename',
        'data-external': '',
      }, doc.filename),
      h('p', { class: 'doc-card-desc' }, doc.description ?? ''),
      ...(ownerMode ? [h('div', { class: 'doc-card-actions' }, [
        h('button', {
          class: 'btn btn-ghost btn-sm',
          type: 'button',
          aria: { label: 'Редактировать описание' },
          on: { click: () => startInlineEdit(doc) },
        }, '✎'),
        h('button', {
          class: 'btn btn-ghost btn-sm',
          type: 'button',
          aria: { label: 'Удалить документ' },
          on: { click: () => openDeleteModal(doc) },
        }, '🗑'),
      ])] : []),
    ]);
  }

  function openDeleteModal(doc: DocumentSummary): void {
    const modal = openModal({
      title: 'Удалить документ?',
      size: 'sm',
      content: (close) => h('div', null, [
        h('p', null, `«${doc.filename}» будет удалён. Это действие нельзя отменить.`),
        h('div', { class: 'modal-footer', style: 'margin-top: var(--space-4); padding: 0;' }, [
          h('button', {
            class: 'btn btn-secondary',
            type: 'button',
            on: { click: () => close() },
          }, 'Отмена'),
          h('button', {
            class: 'btn btn-danger',
            type: 'button',
            on: {
              click: async () => {
                close();
                // Откат возвращает одну карточку на её место, а не снимок всего
                // массива: модалка закрывается до await, поэтому второе удаление
                // можно подтвердить, пока первый DELETE в полёте. Снимок
                // воскресил бы карточку, которую второй запрос уже успешно снёс.
                // Место ищется заново — по соседу снизу, а не по сохранённому
                // индексу: за время запроса список мог сдвинуться (соседнее
                // удаление, загрузка добавляет новый файл в начало).
                const index = docs.findIndex((d) => d.id === doc.id);
                if (index === -1) return;
                const removed = docs[index]!;
                const nextId = docs[index + 1]?.id ?? null;
                docs = docs.filter((d) => d.id !== doc.id);
                renderDocs();
                try {
                  await data.deleteDocument(doc.id);
                  toast('Документ удалён', { type: 'success' });
                } catch (err) {
                  if (err instanceof ApiError && err.status === 404) {
                    toast('Документ уже был удалён', { type: 'info' });
                    return;
                  }
                  const at = nextId == null ? -1 : docs.findIndex((d) => d.id === nextId);
                  docs = at === -1
                    ? [...docs, removed]
                    : [...docs.slice(0, at), removed, ...docs.slice(at)];
                  renderDocs();
                  toast('Не удалось удалить документ', { type: 'error' });
                }
              },
            },
          }, 'Удалить'),
        ]),
      ]),
    });
    void modal;
  }

  function openUploadModal(): void {
    const modal = openModal({
      title: 'Загрузить документ',
      size: 'md',
      content: (close) => buildUploadForm(close),
    });
    void modal;
  }

  function buildUploadForm(close: () => void): HTMLElement {
    let selectedFile: File | null = null;

    const fileInput = h('input', {
      type: 'file',
      accept: 'application/pdf,.pdf',
      style: 'display: none;',
    }) as HTMLInputElement;

    const selectedFileLine = h('div', {
      class: 'doc-selected-file',
      style: 'display: none;',
    });

    const dropzone = h('div', {
      class: 'doc-dropzone',
      role: 'button',
      tabindex: '0',
      aria: { label: 'Загрузить PDF документ' },
    }, [
      h('div', { style: 'font-size: 2rem; color: var(--text-tertiary);' }, '📎'),
      h('p', null, 'Перетащите PDF сюда или нажмите для выбора'),
      h('p', { class: 'doc-desc-counter' }, 'Только PDF · не более 10 МБ'),
      fileInput,
    ]);

    const descArea = h('textarea', {
      class: 'form-textarea',
      'aria-label': 'Описание документа',
      placeholder: '',
      style: 'min-height: 72px; resize: vertical;',
    }) as HTMLTextAreaElement;

    const descCounter = h('div', {
      class: 'doc-desc-counter',
      'aria-live': 'polite',
    }, `символов: 0 / ${MAX_DESC_LEN}`);

    descArea.addEventListener('input', () => {
      if (descArea.value.length > MAX_DESC_LEN) {
        descArea.value = descArea.value.slice(0, MAX_DESC_LEN);
      }
      descCounter.textContent = `символов: ${descArea.value.length} / ${MAX_DESC_LEN}`;
    });

    const submitBtn = h('button', {
      class: 'btn btn-primary',
      type: 'button',
      disabled: true,
    }, 'Загрузить') as HTMLButtonElement;

    const setFile = (file: File) => {
      if (file.type && file.type !== 'application/pdf' && !file.name.endsWith('.pdf')) {
        toast('Принимаем только PDF-файлы', { type: 'error' });
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        toast('Файл превышает 10 МБ', { type: 'error' });
        return;
      }
      selectedFile = file;
      selectedFileLine.style.display = 'flex';
      selectedFileLine.innerHTML = '';
      selectedFileLine.append(
        h('span', null, file.name),
        h('button', {
          class: 'btn btn-ghost btn-sm',
          type: 'button',
          aria: { label: 'Убрать файл' },
          on: {
            click: () => {
              selectedFile = null;
              selectedFileLine.style.display = 'none';
              submitBtn.disabled = true;
              fileInput.value = '';
            },
          },
        }, '✕'),
      );
      submitBtn.disabled = false;
    };

    dropzone.addEventListener('click', (ev) => {
      if ((ev.target as HTMLElement).closest('button')) return;
      fileInput.click();
    });
    dropzone.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); fileInput.click(); }
    });
    dropzone.addEventListener('dragover', (ev) => {
      ev.preventDefault();
      dropzone.classList.add('dragover');
    });
    dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'));
    dropzone.addEventListener('drop', (ev) => {
      ev.preventDefault();
      dropzone.classList.remove('dragover');
      const file = ev.dataTransfer?.files[0];
      if (file) setFile(file);
    });
    fileInput.addEventListener('change', () => {
      const file = fileInput.files?.[0];
      if (file) setFile(file);
    });

    submitBtn.addEventListener('click', async () => {
      if (!selectedFile) return;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Загружаем…';
      dropzone.style.pointerEvents = 'none';
      try {
        const uploaded = await data.uploadDocument(selectedFile, descArea.value.trim() || undefined);
        docs = [uploaded, ...docs.filter((d) => d.id !== uploaded.id)];
        updateSubtitle();
        renderDocs();
        close();
        toast('Документ загружен', { type: 'success' });
      } catch {
        toast('Ошибка загрузки. Попробуйте ещё раз', { type: 'error' });
        submitBtn.disabled = false;
        submitBtn.textContent = 'Загрузить';
        dropzone.style.pointerEvents = '';
      }
    });

    return h('div', null, [
      dropzone,
      selectedFileLine,
      h('div', { class: 'form-field', style: 'margin-top: var(--space-4);' }, [
        h('label', { class: 'form-label' }, 'Описание (необязательно)'),
        descArea,
        descCounter,
      ]),
      h('div', { class: 'modal-footer', style: 'padding: 0; margin-top: var(--space-4);' }, [
        h('button', {
          class: 'btn btn-secondary',
          type: 'button',
          on: { click: () => close() },
        }, 'Отмена'),
        submitBtn,
      ]),
    ]);
  }

  function updateSubtitle(): void {
    if (!docs.length) {
      subtitleEl.textContent = 'Пока нет документов';
      return;
    }
    const sorted = [...docs].sort(
      (a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime(),
    );
    const last = sorted[0]!;
    subtitleEl.textContent = `${docs.length} ${plural(docs.length, ['файл', 'файла', 'файлов'])} · последний загружен ${formatDate(last.uploaded_at)}`;
  }

  void (async () => {
    try {
      docs = await data.listDocuments();
      updateSubtitle();
      renderDocs();
      document.dispatchEvent(new Event('route:settled'));
    } catch {
      subtitleEl.textContent = '';
      contentEl.innerHTML = '';
      contentEl.appendChild(h('div', { class: 'empty-state', role: 'alert' }, [
        h('p', null, 'Не удалось загрузить список документов.'),
        h('button', {
          class: 'btn btn-secondary mt-2',
          type: 'button',
          on: { click: () => navigate('/documents', { replace: true }) },
        }, 'Повторить'),
      ]));
    }
  })();

  return root;
}

function renderEmpty(isOwner: boolean, onUpload: () => void): HTMLElement {
  if (isOwner) {
    return h('div', { class: 'empty-state' }, [
      h('p', null, 'Медицинские документы появятся здесь.'),
      h('p', { class: 'empty-hint' }, 'Загрузите первый — выписку, заключение, любой PDF, который нужен доктору.'),
      h('button', {
        class: 'btn btn-primary mt-4',
        type: 'button',
        on: { click: onUpload },
      }, '+ Загрузить первый документ'),
    ]);
  }
  return h('div', { class: 'empty-state' }, [
    h('p', null, 'Документы пока не загружены.'),
    h('p', { class: 'empty-hint' }, 'Иван добавит их здесь.'),
  ]);
}
